import React, { useMemo, useState } from 'react';
import styles from './index.module.scss';
import SubjectProfileTimelineTab from './tabs/timeline';
import Card from '@components-clean/core/card';
import SubjectProfileActionBar, {
    SubjectProfileTabStorage,
} from '@components-clean/subject-monitoring/subject-profile/subject-profile-action-bar';
import SubjectProfileSideContent from '@components-clean/subject-monitoring/subject-profile/subject-profile-side-content';
import { usePageToolbar } from '@contexts-clean/page-toolbar-context';
import { useOnValueChange, useStorageState } from '@hooks-clean/index';
import { ISMFormData, SubjectMonitoringContext } from '@pages-clean/central-monitoring/interfaces/context';
import SubjectMonitoringWrapper, {
    SubjectMonitoringContentProps,
} from '@pages-clean/subject-monitoring/subject-profile/subject-monitoring-wrapper';
import DataListing from '@pages-clean/subject-monitoring/subject-profile/tabs/datalisting';
import SubjectMonitoringNotesGrid from '@pages-clean/subject-monitoring/subject-profile/tabs/notes';
import SubjectProfileTagsGrid from '@pages-clean/subject-monitoring/subject-profile/tabs/tags/subject-profile-tags-grid';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { LoggedInPageProps } from '@config/routes/interfaces';

const Content = ({ orgRef, studyRef, studyNumber, cm2Dataset }: SubjectMonitoringContentProps) => {
    const user = useAuthorization();
    const canEdit =
        user?.canWrite?.({
            module: Constants.Modules.SM,
            element: Constants.DataElements.SubjectMonitoring,
            permission: Permissions.ReadWrite,
        }) ?? false;

    const [selectedTab, setSelectedTab] = useStorageState(SubjectProfileTabStorage);

    const { state } = usePageToolbar();
    const { subjectMonitoringProfileOverviewToggle } = state;

    const [data, setData] = useState<ISMFormData>({
        studyRef,
        dataset: cm2Dataset,
    });

    const siteHubProviderValue = useMemo(
        () => ({
            current: data,
            setCurrent: setData,
        }),
        [data],
    );

    // When dataset changes, update the `data` copy of it and reset selected subject (to none)
    useOnValueChange(cm2Dataset, () => {
        setData((old) => ({
            ...old,
            dataset: cm2Dataset,
            subjectId: undefined,
            subjectCode: undefined,
            subjectNumber: undefined,
        }));
    });

    function getTabContent(): React.ReactNode {
        switch (selectedTab) {
            case 'timeline':
                return (
                    data.subjectId != null && (
                        <SubjectProfileTimelineTab
                            orgRef={orgRef}
                            studyRef={studyRef}
                            datasetId={data.dataset.id}
                            datasetCutDate={data.dataset.dataCutDate}
                            subjectId={data.subjectId}
                        />
                    )
                );

            case 'dataListings':
                return (
                    data.subjectId != null &&
                    data.subjectCode != null &&
                    data.subjectNumber != null && (
                        <DataListing
                            studyRef={studyRef}
                            studyNumber={studyNumber}
                            dataset={data.dataset}
                            subjectId={data.subjectId}
                            subjectCode={data.subjectCode}
                            subjectNumber={data.subjectNumber}
                        />
                    )
                );

            case 'tags':
                return (
                    data.subjectCode != null && (
                        <SubjectProfileTagsGrid
                            canEdit={canEdit}
                            studyRef={studyRef}
                            subjectCode={data.subjectCode}
                            datasetId={data.dataset.id}
                        />
                    )
                );

            case 'notes':
                return (
                    data.subjectCode && (
                        <SubjectMonitoringNotesGrid
                            studyRef={studyRef}
                            canEdit={canEdit}
                            subjectId={data.subjectId!}
                            subjectNumber={data.subjectNumber}
                            datasetId={data.dataset.id}
                            datasetOpenedOn={data.dataset.dataCutDate}
                        />
                    )
                );

            default:
                return null;
        }
    }

    return (
        <SubjectMonitoringContext.Provider value={siteHubProviderValue}>
            <div className={styles.pageWrapper}>
                <div className={styles.pageLayout} data-sidecontent-visible={subjectMonitoringProfileOverviewToggle}>
                    <Card
                        data-testid="main-content"
                        padding="none"
                        bodyClassName={styles.mainContent}
                        // Putting controls in the `title` feels like a hack, but it seemingly works just fine...
                        title={<SubjectProfileActionBar selectedTab={selectedTab} setSelectedTab={setSelectedTab} />}
                    >
                        {getTabContent()}
                    </Card>

                    {data.subjectId && data.subjectCode && data.subjectNumber != null && (
                        <aside className={styles.sideContent} data-visible={subjectMonitoringProfileOverviewToggle}>
                            <SubjectProfileSideContent
                                canEdit={canEdit}
                                studyRef={studyRef}
                                datasetId={data.dataset.id}
                                datasetCreatedDate={data.dataset.created}
                                subjectId={data.subjectId}
                                subjectCode={data.subjectCode}
                                subjectNumber={data.subjectNumber}
                            />
                        </aside>
                    )}
                </div>
            </div>
        </SubjectMonitoringContext.Provider>
    );
};

const SubjectProfile = ({ study, cm2Dataset }: LoggedInPageProps) => (
    <SubjectMonitoringWrapper study={study} cm2Dataset={cm2Dataset} Content={Content} />
);

export default SubjectProfile;
